import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import GA4RouteTracker from "./components/analytics/GA4RouteTracker";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { FranchiseProvider } from "./context/FranchiseContext";
import { HelmetProvider } from "react-helmet-async";

// Public Pages
import HomePage from "./pages/public/HomePage";
import AboutPage from "./pages/public/AboutPage";
import OurStoryPage from "./pages/public/OurStoryPage";
import ProgramsPage from "./pages/public/ProgramsPage";
import LocationsPage from "./pages/public/LocationsPage";
import CatalogPage from "./pages/public/CatalogPage";
import CourseDetailPage from "./pages/public/CourseDetailPage";
import FAQPage from "./pages/public/FAQPage";
import ContactPage from "./pages/public/ContactPage";
import UnsubscribePage from "./pages/public/UnsubscribePage";
import BlogPage from "./pages/public/BlogPage";
import BlogPostPage from "./pages/public/BlogPostPage";
import CommunityImpactPage from "./pages/public/CommunityImpactPage";
import CurriculumPage from "./pages/public/CurriculumPage";
import PartnershipsPage from "./pages/public/PartnershipsPage";
import InstitutionPartnerPage from "./pages/public/InstitutionPartnerPage";
import RoboticsHubPage from "./pages/public/RoboticsHubPage";
import CodingDivisionPage from "./pages/public/CodingDivisionPage";
import EngineeringDivisionPage from "./pages/public/EngineeringDivisionPage";
import SummerCampsPage from "./pages/public/SummerCampsPage";
import CalendarPage from "./pages/public/CalendarPage";
import CampDetailPage from "./pages/public/CampDetailPage";
import MarketplacePage from "./pages/public/MarketplacePage";
import MathDivisionPage from "./pages/public/MathDivisionPage";
import ScienceDivisionPage from "./pages/public/ScienceDivisionPage";
import STEMKitStorePage from "./pages/public/STEMKitStorePage";
import MathArenaPage from "./pages/public/MathArenaPage";
import ExplorePage from "./pages/public/ExplorePage";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ClaimAccountPage from "./pages/auth/ClaimAccountPage";
import TermsPage from "./pages/public/TermsPage";
import PrivacyPage from "./pages/public/PrivacyPage";

// Parent Dashboard Pages
import ParentDashboard from "./pages/parent/ParentDashboard";
import LearnersPage from "./pages/parent/LearnersPage";
import EnrollmentsPage from "./pages/parent/EnrollmentsPage";
import PurchaseHistoryPage from "./pages/parent/PurchaseHistoryPage";
import ParentProgressPage from "./pages/parent/ParentProgressPage";
import PointsPage from "./pages/parent/PointsPage";
import RewardsPage from "./pages/parent/RewardsPage";
import RafflesPage from "./pages/parent/RafflesPage";
import FormsPage from "./pages/parent/FormsPage";
import BadgesPage from "./pages/parent/BadgesPage";
import ReferralPage from "./pages/parent/ReferralPage";
import GamificationDashboard from "./pages/parent/GamificationDashboard";
import CertificatesPage from "./pages/parent/CertificatesPage";
import ReviewsPage from "./pages/parent/ReviewsPage";
import MessagesPage from "./pages/parent/MessagesPage";
import WeeklyReportsPage from "./pages/parent/WeeklyReportsPage";
import ClassFeedPage from "./pages/parent/ClassFeedPage";
import DocumentSignPage from "./pages/parent/DocumentSignPage";
import DocumentsPage from "./pages/parent/DocumentsPage";
import ProfilePage from "./pages/parent/ProfilePage";

// Public Pages - Certificate Verification
import CertificateVerifyPage from "./pages/public/CertificateVerifyPage";
import CertificateLeaderboardPage from "./pages/public/CertificateLeaderboardPage";
import PartnersPage from "./pages/public/PartnersPage";

import AfterSchoolPage from "./pages/public/AfterSchoolPage";
import StudentShowcasePage from "./pages/public/StudentShowcasePage";

// Checkout Pages
import CartPage from "./pages/checkout/CartPage";
import CheckoutSuccessPage from "./pages/checkout/CheckoutSuccessPage";
import CheckoutCancelPage from "./pages/checkout/CheckoutCancelPage";

// Registration Page
import RegistrationPage from "./pages/registration/RegistrationPage";

// Instructor Pages
import InstructorDashboard from "./pages/instructor/InstructorDashboard";
import InstructorDashboardPage from "./pages/instructor/InstructorDashboardPage";
import InstructorSectionPage from "./pages/instructor/InstructorSectionPage";
import InstructorRosterPage from "./pages/instructor/InstructorRosterPage";
import InstructorAttendancePage from "./pages/instructor/InstructorAttendancePage";
import InstructorAssignmentsPage from "./pages/instructor/InstructorAssignmentsPage";
import InstructorCompletionsPage from "./pages/instructor/InstructorCompletionsPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import DatabaseAdminPage from "./pages/admin/DatabaseAdminPage";
import AdminCoursesPage from "./pages/admin/AdminCoursesPage";
import AdminSectionsPage from "./pages/admin/AdminSectionsPage";
import AdminWorkshopsPage from "./pages/admin/AdminWorkshopsPage";
import AdminLocationsPage from "./pages/admin/AdminLocationsPage";
import AdminReportsPage from "./pages/admin/AdminReportsPage";
import AdminImportedDataPage from "./pages/admin/AdminImportedDataPage";
import AdminSawyerDataPage from "./pages/admin/AdminSawyerDataPage";
import NotFoundPage from "./pages/public/NotFoundPage";
import AdminDataCleanupPage from "./pages/admin/AdminDataCleanupPage";
import AdminAIPage from "./pages/admin/AdminAIPage";
import AdminBlogPage from "./pages/admin/AdminBlogPage";
import AdminCRMPage from "./pages/admin/AdminCRMPage";
import AdminChecklistPage from "./pages/admin/AdminChecklistPage";
import AdminFranchisesPage from "./pages/admin/AdminFranchisesPage";
// Consolidated pages with tabs
import AdminPeoplePage from "./pages/admin/AdminPeoplePage";
import AdminAllUsersPage from "./pages/admin/AdminAllUsersPage";
import AdminFormsPage from "./pages/admin/AdminFormsPage";
import AdminLearnersPage from "./pages/admin/AdminLearnersPage";
import AdminRewardsHubPage from "./pages/admin/AdminRewardsHubPage";
import AdminOperationsPage from "./pages/admin/AdminOperationsPage";
import AdminToolsPage from "./pages/admin/AdminToolsPage";
import AdminPartnersHubPage from "./pages/admin/AdminPartnersHubPage";
import AdminCMSPage from "./pages/admin/AdminCMSPage";
import AdminBugFixingPage from "./pages/admin/AdminBugFixingPage";
import AdminSiteSettingsPage from "./pages/admin/AdminSiteSettingsPage";
import AdminPageEditorPage from "./pages/admin/AdminPageEditorPage";
import AdminGuidePage from "./pages/admin/AdminGuidePage";
import AdminWebsiteSettingsPage from "./pages/admin/AdminWebsiteSettingsPage";
import AdminAnalyticsPage from "./pages/admin/AdminAnalyticsPage";
import AdminIntegrationsPage from "./pages/admin/AdminIntegrationsPage";
import AdminEmailSchedulingPage from "./pages/admin/AdminEmailSchedulingPage";
import AdminSchedulingPage from "./pages/admin/AdminSchedulingPage";
import AdminCommunicationsPage from "./pages/admin/AdminCommunicationsPage";
import AdminSTEMCertificatesPage from "./pages/admin/AdminSTEMCertificatesPage";
import AdminCampsPage from "./pages/admin/AdminCampsPage";
import AdminCampsScheduleView from "./pages/admin/AdminCampsScheduleView";
import AdminCampDetailPage from "./pages/admin/AdminCampDetailPage";
import AdminEnrollmentsPage from "./pages/admin/AdminEnrollmentsPage";
import AdminCouponsPage from "./pages/admin/AdminCouponsPage";
import AdminImagesPage from "./pages/admin/AdminImagesPage";
import AdminReviewsPage from "./pages/admin/AdminReviewsPage";
import AdminWeeklyDigestPage from "./pages/admin/AdminWeeklyDigestPage";
import AdminInterestFormsPage from "./pages/admin/AdminInterestFormsPage";
import AdminNewsletterPage from "./pages/admin/AdminNewsletterPage";
import AdminDocumentsPage from "./pages/admin/AdminDocumentsPage";

// Instructor Pages - Checklist
import InstructorChecklistPage from "./pages/instructor/InstructorChecklistPage";

// Parent Pages - add recommendations
import RecommendationsPage from "./pages/parent/RecommendationsPage";

// Partner Pages
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import PartnerReportsPage from "./pages/partner/PartnerReportsPage";

// Layout Components
import MainLayout from "./components/layout/MainLayout";
import DashboardLayout from "./components/layout/DashboardLayout";
import ProtectedRoute from "./components/auth/ProtectedRoute";

import "./App.css";

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <CartProvider>
        <FranchiseProvider>
        <BrowserRouter>
          <GA4RouteTracker />
          <Routes>
            {/* Public Routes */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/explore" element={<Navigate to="/summer-camps" replace />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/programs/robotics" element={<RoboticsHubPage />} />
              <Route path="/programs" element={<ProgramsPage />} />
              <Route path="/programs/:program" element={<ProgramsPage />} />
              <Route path="/locations" element={<LocationsPage />} />
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/courses/:courseId" element={<CourseDetailPage />} />
              <Route path="/our-story" element={<OurStoryPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/community-impact" element={<CommunityImpactPage />} />
              <Route path="/curriculum" element={<CurriculumPage />} />
              <Route path="/partnerships" element={<PartnershipsPage />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/park-district-camps" element={<Navigate to="/summer-camps" replace />} />
              <Route path="/after-school" element={<Navigate to="/summer-camps" replace />} />
              <Route path="/classes" element={<Navigate to="/summer-camps" replace />} />
              <Route path="/partner-with-us" element={<InstitutionPartnerPage />} />
              <Route path="/robotics" element={<RoboticsHubPage />} />
              <Route path="/technology" element={<CodingDivisionPage />} />
              <Route path="/coding" element={<Navigate to="/technology" replace />} />
              <Route path="/engineering" element={<EngineeringDivisionPage />} />
              <Route path="/summer-camps" element={<SummerCampsPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/camps/:id" element={<CampDetailPage />} />
              <Route path="/partner-programs" element={<MarketplacePage />} />
              <Route path="/math" element={<MathDivisionPage />} />
              <Route path="/science" element={<ScienceDivisionPage />} />
              <Route path="/stem-kits" element={<STEMKitStorePage />} />
              <Route path="/math-arena" element={<MathArenaPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/register" element={<RegisterPage />} />
              <Route path="/claim-account" element={<ClaimAccountPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/verify/:certId" element={<CertificateVerifyPage />} />
              <Route path="/leaderboard" element={<CertificateLeaderboardPage />} />
              <Route path="/showcase" element={<StudentShowcasePage />} />
            </Route>

            {/* Cart & Checkout */}
            <Route element={<MainLayout />}>
              <Route path="/register" element={<RegistrationPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
            </Route>

            {/* Parent Dashboard Routes */}
            <Route path="/parent" element={<ProtectedRoute roles={["parent"]}><DashboardLayout role="parent" /></ProtectedRoute>}>
              <Route index element={<ParentDashboard />} />
              <Route path="learners" element={<LearnersPage />} />
              <Route path="progress" element={<ParentProgressPage />} />
              <Route path="enrollments" element={<EnrollmentsPage />} />
              <Route path="purchases" element={<PurchaseHistoryPage />} />
              <Route path="points" element={<PointsPage />} />
              <Route path="rewards" element={<RewardsPage />} />
              <Route path="raffles" element={<RafflesPage />} />
              <Route path="forms" element={<FormsPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="documents/:documentId/sign" element={<DocumentSignPage />} />
              <Route path="badges" element={<BadgesPage />} />
              <Route path="referral" element={<ReferralPage />} />
              <Route path="recommendations" element={<RecommendationsPage />} />
              <Route path="stem-score" element={<GamificationDashboard />} />
              <Route path="certificates" element={<CertificatesPage />} />
              <Route path="reviews" element={<ReviewsPage />} />
              <Route path="messages" element={<MessagesPage />} />
              <Route path="weekly-reports" element={<WeeklyReportsPage />} />
              <Route path="class-feed" element={<ClassFeedPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Instructor Dashboard Routes */}
            <Route path="/instructor" element={<ProtectedRoute roles={["instructor", "admin", "super_admin"]}><DashboardLayout role="instructor" /></ProtectedRoute>}>
              <Route index element={<InstructorDashboardPage />} />
              <Route path="roster" element={<InstructorRosterPage />} />
              <Route path="attendance" element={<InstructorAttendancePage />} />
              <Route path="assignments" element={<InstructorAssignmentsPage />} />
              <Route path="completions" element={<InstructorCompletionsPage />} />
              <Route path="checklists" element={<InstructorChecklistPage />} />
              <Route path="sections/:sectionId" element={<InstructorSectionPage />} />
              <Route path="messages" element={<MessagesPage />} />
            </Route>

            {/* Admin Dashboard Routes */}
            <Route path="/admin" element={<ProtectedRoute roles={["admin", "super_admin"]}><DashboardLayout role="admin" /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="database" element={<DatabaseAdminPage />} />
              <Route path="courses" element={<AdminCoursesPage />} />
              <Route path="scheduling" element={<AdminSchedulingPage />} />
              <Route path="sections" element={<AdminSectionsPage />} />
              <Route path="workshops" element={<AdminWorkshopsPage />} />
              <Route path="locations" element={<AdminLocationsPage />} />
              <Route path="franchises" element={<AdminFranchisesPage />} />
              <Route path="partners" element={<AdminPartnersHubPage />} />
              <Route path="checklists" element={<AdminChecklistPage />} />
              {/* Consolidated pages with tabs */}
              <Route path="users" element={<Navigate to="/admin/people?tab=users" replace />} />
              <Route path="forms" element={<AdminFormsPage />} />
              <Route path="people" element={<AdminPeoplePage />} />
              <Route path="parents" element={<AdminPeoplePage />} />
              <Route path="staff" element={<AdminPeoplePage />} />
              <Route path="staff/instructors" element={<AdminPeoplePage />} />
              <Route path="staff/volunteers" element={<AdminPeoplePage />} />
              <Route path="staff/internal" element={<AdminPeoplePage />} />
              <Route path="staff/all" element={<AdminPeoplePage />} />
              <Route path="learners" element={<Navigate to="/admin/people?tab=learners" replace />} />
              <Route path="rewards" element={<AdminRewardsHubPage />} />
              <Route path="operations" element={<AdminOperationsPage />} />
              <Route path="tools" element={<AdminToolsPage />} />
              {/* Reports & Content */}
              <Route path="reports" element={<AdminReportsPage />} />
              <Route path="imported-data" element={<AdminImportedDataPage />} />
              <Route path="sawyer-data" element={<AdminSawyerDataPage />} />
              <Route path="data-cleanup" element={<AdminDataCleanupPage />} />
              <Route path="blog" element={<AdminBlogPage />} />
              <Route path="communications" element={<AdminCommunicationsPage />} />
              <Route path="ai" element={<AdminAIPage />} />
              <Route path="cms" element={<AdminCMSPage />} />
              <Route path="bugs" element={<AdminBugFixingPage />} />
              <Route path="site-settings" element={<AdminWebsiteSettingsPage />} />
              <Route path="edit-pages" element={<AdminWebsiteSettingsPage />} />
              <Route path="website-settings" element={<AdminWebsiteSettingsPage />} />
              <Route path="help" element={<AdminGuidePage />} />
              <Route path="analytics" element={<AdminAnalyticsPage />} />
              <Route path="integrations" element={<AdminIntegrationsPage />} />
              <Route path="email-scheduling" element={<AdminEmailSchedulingPage />} />
              <Route path="camps" element={<AdminCampsPage />} />
              <Route path="camps/schedule" element={<AdminCampsScheduleView />} />
              <Route path="camps/:campId" element={<AdminCampDetailPage />} />
              <Route path="enrollments" element={<AdminEnrollmentsPage />} />
              <Route path="coupons" element={<AdminCouponsPage />} />
              <Route path="weekly-digest" element={<AdminWeeklyDigestPage />} />
              <Route path="images" element={<AdminImagesPage />} />
              <Route path="reviews" element={<AdminReviewsPage />} />
              <Route path="stem-certificates" element={<AdminSTEMCertificatesPage />} />
              <Route path="interest-forms" element={<AdminInterestFormsPage />} />
              <Route path="newsletter" element={<AdminNewsletterPage />} />
              <Route path="documents" element={<AdminDocumentsPage />} />
            </Route>

            {/* Partner Dashboard Routes */}
            <Route path="/partner" element={<ProtectedRoute roles={["partner", "admin", "super_admin"]}><DashboardLayout role="partner" /></ProtectedRoute>}>
              <Route index element={<PartnerDashboard />} />
              <Route path="reports" element={<PartnerReportsPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </FranchiseProvider>
      </CartProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
